/**
 * /frontend/src/shared/utils/merchantHistoryWindow.ts
 *
 * Single source of truth for how far into the past a merchant may navigate
 * anywhere in the portal — analytics date ranges, the appointments calendar,
 * billing history, membership ledger filters, and any future surface that
 * exposes a "go to the past" control.
 *
 * Product rule: a merchant can look back at most 6 months. Wherever a past
 * option is feasible, the earliest selectable day is today minus 6 months.
 */

/** Maximum number of months of history a merchant may browse into the past. */
export const MERCHANT_HISTORY_MONTHS = 6;

/** Default look-back for history exports — merchants can widen up to the max. */
export const DEFAULT_HISTORY_MONTHS = 1;

const pad = (value: number): string => String(value).padStart(2, '0');

/** Formats a Date to a local YYYY-MM-DD string. */
const toLocalIsoDate = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/**
 * `reference` (defaults to now) minus `months`, as a local Date at midnight.
 * @param {number} months
 * @param {Date} reference
 * @returns {Date}
 */
export const monthsAgoDate = (months: number, reference: Date = new Date()): Date => {
  const anchor = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
  anchor.setMonth(anchor.getMonth() - months);
  return anchor;
};

/** `reference` minus `months` as a YYYY-MM-DD string. */
export const monthsAgoIso = (months: number, reference: Date = new Date()): string =>
  toLocalIsoDate(monthsAgoDate(months, reference));

/**
 * The earliest calendar day a merchant may select, as a local Date at midnight.
 * Anchored to `reference` (defaults to now) minus MERCHANT_HISTORY_MONTHS.
 * @param {Date} reference
 * @returns {Date}
 */
export const earliestMerchantPastDate = (reference: Date = new Date()): Date =>
  monthsAgoDate(MERCHANT_HISTORY_MONTHS, reference);

/**
 * The earliest selectable past day as a YYYY-MM-DD string — use as the `min`
 * attribute on `<input type="date">` controls that reach into the past.
 * @param {Date} reference
 * @returns {string}
 */
export const earliestMerchantPastDateIso = (reference: Date = new Date()): string =>
  toLocalIsoDate(earliestMerchantPastDate(reference));

/**
 * True when `date` falls before the earliest allowed past day (i.e. the
 * merchant is trying to look back further than MERCHANT_HISTORY_MONTHS).
 * @param {Date} date
 * @param {Date} reference
 * @returns {boolean}
 */
export const isBeforeMerchantHistoryWindow = (date: Date, reference: Date = new Date()): boolean => {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return day.getTime() < earliestMerchantPastDate(reference).getTime();
};
